import { motion } from "framer-motion";
import { MessagesSquare, Hammer, Gauge, HeartHandshake } from "lucide-react";

const ENSO_IMG = "/art/enso.webp";

const principles = [
  {
    icon: MessagesSquare,
    title: "Plain English",
    copy: "Every step explained without the tech-speak.",
  },
  {
    icon: Hammer,
    title: "Built to last",
    copy: "Clean, modern code that won't need rebuilding next year.",
  },
  {
    icon: Gauge,
    title: "Fast by default",
    copy: "Pages that load quickly so visitors stay and Google pays attention.",
  },
  {
    icon: HeartHandshake,
    title: "Care after launch",
    copy: "Updates, backups and small fixes handled for you.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const About = ({ embedded = false }) => (
  <section id="about" className={`relative overflow-hidden ${embedded ? "pt-2" : "py-24 lg:py-32"}`}>
    <div className={`relative mx-auto max-w-7xl ${embedded ? "" : "px-6 lg:px-10"}`}>
      {!embedded && (
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="text-xs font-medium uppercase tracking-[0.3em] text-jade"
        >
          About
        </motion.p>
      )}
      <motion.h2
        {...fadeUp}
        transition={{ duration: 0.7, delay: 0.08 }}
        className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl"
        data-testid="about-heading"
      >
        A studio of one, <em className="italic text-jade">standards</em> of a
        team.
      </motion.h2>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7 }}
          className="rounded-2xl bg-white p-10 shadow-ambient md:col-span-7 lg:p-14"
          data-testid="about-story-card"
        >
          <p className="text-lg leading-relaxed text-ink2">
            Vagus Digital is an independent studio run by a sole trader. I build
            the digital side of small businesses across the UK and beyond.
            Websites, AI tools that save you hours each week, and the SEO and
            social presence that brings customers through the door. You brief me,
            I build it, and I stay around to look after it. The person you talk
            to is the person writing your code.
          </p>
          <p className="mt-6 leading-relaxed text-ink2/80">
            You deal with one person from start to finish. Clear pricing, real
            timelines and a website that does your business justice.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7, delay: 0.12 }}
          className="relative flex min-h-[300px] items-center justify-center overflow-hidden rounded-2xl bg-paper2 shadow-ambient md:col-span-5"
          data-testid="about-enso-card"
        >
          <img
            src={ENSO_IMG}
            alt="Hand-brushed jade green enso circle"
            className="-mt-10 w-48 opacity-80 lg:w-56"
          />
          <p className="absolute bottom-6 left-6 font-display text-2xl font-light italic text-ink">
            Honest work, quietly done.
          </p>
        </motion.div>

        {principles.map((p, i) => (
          <motion.div
            key={p.title}
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.08 * i }}
            className="rounded-2xl border border-jade/10 bg-paper2/60 p-8 transition-colors duration-300 hover:bg-paper2 md:col-span-3"
            data-testid={`about-principle-${i}`}
          >
            <p.icon className="h-6 w-6 text-jade" strokeWidth={1.75} />
            <h3 className="mt-5 font-display text-xl font-medium text-ink">
              {p.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-ink2/80">
              {p.copy}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default About;